/**
 * Remembers whether this browser may hold a session, so that startup does not ask
 * the server to restore one that cannot exist.
 *
 * The refresh token is an httpOnly cookie, invisible to scripts by design. This
 * flag carries no secret: it only records that a sign-in happened here.
 *
 * It is kept in two places: local storage, and a cookie. When the parts of the
 * installation have hosts of their own, the session cookie is for all of them (one
 * sign-in at the front), so the flag cookie is too: a cookie of the whole domain,
 * which a page of any host can read. Without it a host nobody signed in at would not
 * try, and would send a signed-in person back to the sign-in form. At one address the
 * cookie is that address's own: the static pages beside the application read the flag
 * from it there as well (site/session.ts).
 *
 * Its value says whose the session is: `1` an account's, `guest` a guest's (public
 * lessons tried without an account). The static pages of the front read it to offer
 * "continue" instead of "sign in", which a guest has yet to do.
 */
import { baseDomain } from '@/shared/lib/site';

const KEY = 'easymusic:signed-in';
const COOKIE = 'easymusic_signed_in';
// As long as a refresh token lives: a flag that outlives it only costs one request.
const COOKIE_SECONDS = 30 * 86_400;

/** Whose session this browser holds: an account's, a guest's, or none. */
export type SessionKind = 'account' | 'guest' | null;

const VALUES: Record<'account' | 'guest', string> = { account: '1', guest: 'guest' };

const hasCookie = () =>
  document.cookie.split('; ').some((pair) => Object.values(VALUES).some((value) => pair === `${COOKIE}=${value}`));

export function mayHaveSession(): boolean {
  try {
    return localStorage.getItem(KEY) !== null || hasCookie();
  } catch {
    return true; // storage unavailable: ask the server
  }
}

export function rememberSession(kind: SessionKind): void {
  try {
    if (kind) localStorage.setItem(KEY, VALUES[kind]);
    else localStorage.removeItem(KEY);
  } catch {
    // Without storage the app simply asks the server on every start.
  }
  const domain = baseDomain();
  const value = kind ? VALUES[kind] : '';
  const age = kind ? COOKIE_SECONDS : 0;
  const scope = domain ? `; Domain=${domain}` : '';
  document.cookie = `${COOKIE}=${value}${scope}; Path=/; Max-Age=${age}; SameSite=Lax`;
}
